/**
 * In-browser teamwork backend for mock mode (screens, tests): notes, tags, follow-up, settings — working on the
 * same mock chat db as ../mock.ts — plus a small presence simulator (ศิริ typing in คุณมายด์'s chat, จิต looking
 * at Pim's) so the collision guard shows on the screens.
 */
import { ApiError } from '../client'
import type { MockChatApi } from '../mock'
import { applyMockTags, MOCK_TAGS, STAFF } from '../mockData'
import type { ChatMessage, ConversationDetail, SendResult, SystemEvent, TagTone } from '../types'
import { tabOf, toSummary } from '../../lib/inbox'
import type {
  AlertSettings,
  AlertValues,
  CollabApi,
  CollabEvent,
  FollowUpInfo,
  FollowUpSendBody,
  Note,
  PresenceItem,
  TagDef,
  TagList,
} from './types'

const clone = <T>(v: T): T => structuredClone(v)
const HOUR_MS = 3600000

export const MOCK_TEMPLATE =
  'สวัสดีค่ะ แอดมิน Cleanfit ทักมาสอบถามค่ะ คุณลูกค้ายังสนใจแพ็กอาหารคลีนอยู่ไหมคะ หากมีคำถามเพิ่มเติม สอบถามแอดมินได้เลยนะคะ'

// ---------------------------------------------------------------- mock realtime bus (presence + collab events)

type Listener = (e: CollabEvent) => void
const listeners = new Set<Listener>()

export function onMockCollab(fn: Listener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function emit(e: CollabEvent): void {
  for (const fn of listeners) fn(clone(e))
}

/** What other screens are doing in mock mode (repeated like real heartbeats while anyone listens). */
export const MOCK_PRESENCE: PresenceItem[] = [
  { conversation_id: 'c102', staff: { id: STAFF.siri.id, name: STAFF.siri.name }, state: 'typing', ttl: 8 },
  { conversation_id: 'c101', staff: { id: STAFF.jit.id, name: STAFF.jit.name }, state: 'viewing', ttl: 10 },
]

/** Start the simulated colleagues; returns a stop function. `everyMs` 0 = one snapshot only (tests). */
export function startMockPresence(everyMs = 5000): () => void {
  emit({ type: 'presence.snapshot', items: MOCK_PRESENCE })
  if (everyMs <= 0) return () => undefined
  const t = setInterval(() => {
    for (const p of MOCK_PRESENCE) emit({ type: 'presence', ...p })
  }, everyMs)
  return () => clearInterval(t)
}

/** A screen's own presence message comes back like the server fans it out (so two tabs see each other). */
export function mockPresenceEcho(msg: unknown, me: { id: string; name: string } | null): void {
  const m = msg as { type?: string; conversation_id?: string; state?: string }
  if (!me || m.type !== 'presence' || !m.conversation_id) return
  if (m.state !== 'viewing' && m.state !== 'typing' && m.state !== 'left') return
  emit({ type: 'presence', conversation_id: m.conversation_id, staff: me, state: m.state, ttl: m.state === 'typing' ? 8 : 10 })
}

// ---------------------------------------------------------------- the API

const DEFAULT_VALUES: AlertValues = {
  overdue_min: 3,
  telegram_on: false,
  telegram_wait_min: 10,
  telegram_queue_over: 0,
  follow_up_after_min: 60,
  follow_up_ai: true,
  follow_up_template: MOCK_TEMPLATE,
  auto_allowed: false,
  auto_send: false,
  meta_human_agent: false,
}

export interface MockCollabOptions {
  latencyMs?: number
  clock?: () => number
}

export class MockCollabApi implements CollabApi {
  private readonly latency: number
  private readonly now: () => number
  private tags: TagDef[]
  private notes: Note[]
  private values: AlertValues = { ...DEFAULT_VALUES }
  private autoOn = new Set<string>()
  private seq = 5000
  /** Tests: the role the settings page sees (owner edits everything, head all but owner-only, staff nothing). */
  role: 'owner' | 'head' | 'staff' = 'owner'

  constructor(
    private readonly chat: MockChatApi,
    opts: MockCollabOptions = {},
  ) {
    this.latency = opts.latencyMs ?? 120
    this.now = opts.clock ?? (() => Date.now())
    this.tags = MOCK_TAGS.map((t) => ({ ...t, count: 0 }))
    const t = this.now()
    const iso = (msAgo: number) => new Date(t - msAgo).toISOString()
    this.notes = [
      {
        id: 'n1', conversation_id: 'c102', contact_id: 'ct-c102', text: 'ลูกค้าแพ้กุ้ง สั่งต่อทุกสิ้นเดือน ชอบรอบส่งเช้า',
        at: iso(200 * 1000), edited_at: null, author: { id: STAFF.ploy.id, name: STAFF.ploy.name }, can_edit: true,
      },
      {
        id: 'n2', conversation_id: 'c102', contact_id: 'ct-c102', text: 'ส่งที่บ้านบางนา ให้โทรก่อนถึง 10 นาที',
        at: iso(44 * 24 * HOUR_MS), edited_at: null, author: { id: STAFF.jit.id, name: STAFF.jit.name }, can_edit: false,
      },
    ]
  }

  private async delay(): Promise<void> {
    if (this.latency > 0) await new Promise((r) => setTimeout(r, this.latency))
  }

  private conv(id: string): ConversationDetail {
    const c = this.chat.db.conversations.get(id)
    if (!c) throw new ApiError(404, 'ไม่พบห้องแชทนี้')
    return c
  }

  private counted(): TagDef[] {
    const counts = new Map<string, number>()
    for (const c of this.chat.db.conversations.values()) for (const t of c.tags ?? []) counts.set(t.id, (counts.get(t.id) ?? 0) + 1)
    return this.tags.map((t) => ({ ...t, count: counts.get(t.id) ?? 0 })).sort((a, b) => a.sort - b.sort)
  }

  private manage(): void {
    if (this.role === 'staff') throw new ApiError(403, 'เฉพาะผู้จัดการและเจ้าของเท่านั้น')
  }

  /** Re-apply a changed definition to every customer carrying it and push the rows like the server does. */
  private async refreshRows(tagId?: string): Promise<void> {
    for (const c of this.chat.db.conversations.values()) {
      if (!c.tags?.length || (tagId && !c.tags.some((t) => t.id === tagId))) continue
      applyMockTags(c, c.tags.map((t) => t.id).filter((id) => this.tags.some((d) => d.id === id)), this.tags)
      await this.chat.updateConversation(c.id, {})
    }
  }

  // ---------------------------------------------------------------- notes
  async listNotes(convId: string): Promise<Note[]> {
    await this.delay()
    const c = this.conv(convId)
    const mine = this.notes.filter((n) => (c.contact.id ? n.contact_id === c.contact.id : n.conversation_id === convId))
    return clone(mine.sort((a, b) => b.at.localeCompare(a.at)))
  }

  async addNote(convId: string, text: string): Promise<Note> {
    await this.delay()
    const c = this.conv(convId)
    const t = text.trim()
    if (!t) throw new ApiError(422, 'โน้ตว่าง')
    if (t.length > 1000) throw new ApiError(422, 'โน้ตยาวเกิน 1,000 ตัวอักษร')
    const me = STAFF[this.chat.db.meId as keyof typeof STAFF] ?? STAFF.ploy
    const note: Note = {
      id: `n${++this.seq}`, conversation_id: convId, contact_id: c.contact.id, text: t,
      at: new Date(this.now()).toISOString(), edited_at: null, author: { id: me.id, name: me.name }, can_edit: true,
    }
    this.notes.push(note)
    emit({ type: 'note.changed', action: 'created', note_id: note.id, conversation_id: convId, contact_id: note.contact_id })
    return clone(note)
  }

  async editNote(noteId: string, text: string): Promise<Note> {
    await this.delay()
    const n = this.notes.find((x) => x.id === noteId)
    if (!n) throw new ApiError(404, 'ไม่พบโน้ตนี้')
    if (!n.can_edit) throw new ApiError(403, 'แก้หรือลบได้เฉพาะโน้ตของตัวเอง (ผู้จัดการและเจ้าของแก้ได้ทุกโน้ต)')
    const t = text.trim()
    if (!t) throw new ApiError(422, 'โน้ตว่าง')
    if (t !== n.text) {
      n.text = t
      n.edited_at = new Date(this.now()).toISOString()
      emit({ type: 'note.changed', action: 'updated', note_id: n.id, conversation_id: n.conversation_id, contact_id: n.contact_id })
    }
    return clone(n)
  }

  async deleteNote(noteId: string): Promise<void> {
    await this.delay()
    const i = this.notes.findIndex((x) => x.id === noteId)
    const n = this.notes[i]
    if (!n) throw new ApiError(404, 'ไม่พบโน้ตนี้')
    if (!n.can_edit) throw new ApiError(403, 'แก้หรือลบได้เฉพาะโน้ตของตัวเอง (ผู้จัดการและเจ้าของแก้ได้ทุกโน้ต)')
    this.notes.splice(i, 1)
    emit({ type: 'note.changed', action: 'deleted', note_id: n.id, conversation_id: n.conversation_id, contact_id: n.contact_id })
  }

  // ---------------------------------------------------------------- tags
  async listTags(): Promise<TagList> {
    await this.delay()
    return clone({ items: this.counted(), can_manage: this.role !== 'staff' })
  }

  async createTag(name: string, tone: TagTone): Promise<TagDef> {
    await this.delay()
    this.manage()
    const n = name.split(/\s+/).filter(Boolean).join(' ')
    if (!n) throw new ApiError(422, 'ชื่อแท็กว่าง')
    if (n.length > 24) throw new ApiError(422, 'ชื่อแท็กยาวเกิน 24 ตัวอักษร')
    if (this.tags.some((t) => t.name.toLowerCase() === n.toLowerCase())) throw new ApiError(409, `มีแท็ก "${n}" อยู่แล้ว`)
    const top = Math.max(0, ...this.tags.map((t) => t.sort))
    const def: TagDef = { id: `t${++this.seq}`, name: n, tone, sort: top + 10, count: 0 }
    this.tags.push(def)
    emit({ type: 'tags.changed' })
    return clone(def)
  }

  async updateTag(tagId: string, patch: { name?: string; tone?: TagTone; sort?: number }): Promise<TagDef> {
    await this.delay()
    this.manage()
    const d = this.tags.find((t) => t.id === tagId)
    if (!d) throw new ApiError(404, 'ไม่พบแท็กนี้')
    if (patch.name !== undefined) {
      const n = patch.name.split(/\s+/).filter(Boolean).join(' ')
      if (!n) throw new ApiError(422, 'ชื่อแท็กว่าง')
      if (this.tags.some((t) => t.id !== tagId && t.name.toLowerCase() === n.toLowerCase())) {
        throw new ApiError(409, 'มีแท็กชื่อนี้อยู่แล้ว')
      }
      d.name = n
    }
    if (patch.tone) d.tone = patch.tone
    if (patch.sort !== undefined) d.sort = patch.sort
    emit({ type: 'tags.changed' })
    await this.refreshRows(tagId)
    return clone(this.counted().find((t) => t.id === tagId) ?? d)
  }

  async deleteTag(tagId: string): Promise<void> {
    await this.delay()
    this.manage()
    const i = this.tags.findIndex((t) => t.id === tagId)
    if (i < 0) throw new ApiError(404, 'ไม่พบแท็กนี้')
    this.tags.splice(i, 1)
    emit({ type: 'tags.changed' })
    await this.refreshRows()
  }

  async orderTags(ids: string[]): Promise<TagList> {
    await this.delay()
    this.manage()
    ids.forEach((id, i) => {
      const d = this.tags.find((t) => t.id === id)
      if (d) d.sort = (i + 1) * 10
    })
    emit({ type: 'tags.changed' })
    await this.refreshRows()
    return clone({ items: this.counted(), can_manage: true })
  }

  async setConversationTags(convId: string, tagIds: string[]) {
    await this.delay()
    const c = this.conv(convId)
    if (c.kind === 'group' || !c.contact.id) throw new ApiError(422, 'แชทกลุ่มติดแท็กลูกค้าไม่ได้')
    const ids = [...new Set(tagIds)]
    if (ids.length > 8) throw new ApiError(422, 'ติดแท็กได้ไม่เกิน 8 แท็กต่อลูกค้า')
    if (!ids.every((id) => this.tags.some((t) => t.id === id))) {
      throw new ApiError(422, 'มีแท็กที่ถูกลบไปแล้ว โหลดหน้าใหม่แล้วลองอีกครั้ง')
    }
    applyMockTags(c, ids, this.tags)
    const conversation = await this.chat.updateConversation(convId, {})
    return { conversation }
  }

  // ---------------------------------------------------------------- follow-up
  private lastIn(convId: string): string | null {
    const items = this.chat.db.threads.get(convId) ?? []
    const m = [...items].reverse().find((x): x is ChatMessage => x.kind === 'message' && x.direction === 'in')
    return m?.at ?? null
  }

  private lastOut(convId: string): string | null {
    const items = this.chat.db.threads.get(convId) ?? []
    const m = [...items].reverse().find((x): x is ChatMessage => x.kind === 'message' && x.direction === 'out')
    return m?.at ?? null
  }

  private rule(c: ConversationDetail): FollowUpInfo['rule'] {
    const name = c.channel === 'ig' ? 'Instagram' : 'Messenger'
    if (c.kind === 'comment') {
      return { ok: false, via: null, send_until: null, note: null,
               reason: 'ห้องคอมเมนต์ ทักตามทางแชทจากหน้านี้ไม่ได้ ตอบใต้คอมเมนต์ในหน้าคอมเมนต์แทน' }
    }
    // web chat (api/app/collab/followup.py): the e-mail notice reaches a visitor who left an address — the demo's do
    if (c.channel === 'web') {
      return { ok: true, via: 'web', send_until: null, reason: null,
               note: 'ข้อความเข้าห้องแชทเว็บของลูกค้า ถ้าลูกค้าไม่ได้เปิดหน้าเว็บอยู่ ระบบแจ้งทางอีเมลที่ลูกค้าให้ไว้ (วันละ 1 ฉบับ 08:30–22:00)' }
    }
    if (c.channel === 'line') {
      return { ok: false, via: 'push', send_until: null, note: null,
               reason: 'บัญชี LINE นี้ปิดการส่งแบบ push (ช่วงทดสอบ) ทักตามต้องใช้ push เพราะตอบฟรีได้เฉพาะไม่เกิน 1 นาทีหลังลูกค้าทัก' }
    }
    const last = this.lastIn(c.id)
    if (!last) return { ok: false, via: null, send_until: null, note: null, reason: `ลูกค้ายังไม่เคยทักมา ${name} ไม่ให้ร้านเริ่มแชทก่อน` }
    const until = Date.parse(last) + 24 * HOUR_MS
    if (this.now() < until - 60000) {
      return { ok: true, via: 'standard', send_until: new Date(until).toISOString(), reason: null,
               note: `${name} ให้ร้านทักได้ภายใน 24 ชม. หลังลูกค้าทักล่าสุด` }
    }
    const agentUntil = Date.parse(last) + 7 * 24 * HOUR_MS
    if (this.values.meta_human_agent && this.now() < agentUntil - 60000) {
      return { ok: true, via: 'human_agent', send_until: new Date(agentUntil).toISOString(), reason: null,
               note: 'เกิน 24 ชม. แล้ว ส่งแบบพนักงานตอบเอง (Human Agent) ได้ภายใน 7 วันหลังลูกค้าทักล่าสุด' }
    }
    return { ok: false, via: null, send_until: null, note: null,
             reason: `เกิน 24 ชม. หลังลูกค้าทักล่าสุด ${name} ให้ทักได้ภายใน 24 ชม. เท่านั้น (แอปของร้านยังไม่ได้สิทธิ์ Human Agent จาก Meta ที่ขยายเป็น 7 วัน)` }
  }

  async getFollowUp(convId: string): Promise<FollowUpInfo> {
    await this.delay()
    const c = this.conv(convId)
    const due = tabOf(c, this.now()) === 'follow_up'
    const name = c.contact.initial !== '#' ? c.contact.name.replace(/^คุณ\s*/, '') : null
    const khun = name ? `คุณ${/^[\u0E00-\u0E7F]/.test(name) ? '' : ' '}${name}` : ''
    const aiText = name
      ? `สวัสดีค่ะ${khun} ไม่ทราบว่าได้ดูเมนูที่ส่งไปแล้วหรือยังคะ ถ้าสนใจแพ็กไหน แอดมินช่วยเลือกให้ได้เลยนะคะ`
      : 'ไม่ทราบว่าได้ดูเมนูที่ส่งไปแล้วหรือยังคะ ถ้าสนใจแพ็กไหน แอดมินช่วยเลือกให้ได้เลยนะคะ'
    const draft: FollowUpInfo['draft'] = !due
      ? null
      : this.values.follow_up_ai && c.channel !== 'line'
        ? { text: aiText, source: 'ai', canned_id: null, label: 'AI ร่างจากแชทนี้' }
        : { text: this.values.follow_up_template, source: 'template', canned_id: null, label: 'ข้อความทักตามตั้งต้นของทีม' }
    const rule = this.rule(c)
    // server: the automatic follow-up never uses Human Agent (app/collab/followup.py auto_held_reason)
    const held = this.autoOn.has(convId) && rule.via === 'human_agent'
      ? `เกิน 24 ชม. หลังลูกค้าทักล่าสุด ${c.channel === 'ig' ? 'Instagram' : 'Messenger'} ไม่ให้ระบบส่งเอง ให้พนักงานตอบ`
      : null
    return clone({
      conversation_id: convId,
      due,
      due_at: c.follow_up_due_at,
      silent_since: this.lastOut(convId),
      rule,
      draft,
      auto: { allowed: this.values.auto_allowed, enabled: this.autoOn.has(convId),
              sending: this.values.auto_allowed && this.values.auto_send, reason: held },
    })
  }

  private markDone(c: ConversationDetail, reason: 'sent' | 'skipped'): void {
    c.follow_up_due_at = null
    const ev: SystemEvent = {
      kind: 'system', id: `e${++this.seq}`, conversation_id: c.id, at: new Date(this.now() + 1).toISOString(),
      event: 'follow_up', staff: { id: STAFF.ploy.id, name: STAFF.ploy.name }, reason,
    }
    this.chat.db.threads.get(c.id)?.push(ev)
  }

  async sendFollowUp(convId: string, body: FollowUpSendBody): Promise<SendResult> {
    const c = this.conv(convId)
    const wasDue = tabOf(c, this.now()) === 'follow_up'
    const rule = this.rule(c)
    if (wasDue && !rule.ok) throw new ApiError(422, rule.reason ?? 'ทักตามในแชทนี้ไม่ได้')
    if (!wasDue) throw new ApiError(409, 'แชทนี้ไม่อยู่ในคิวทักตามแล้ว (ลูกค้าตอบกลับมาแล้ว หรือทักตามไปแล้ว)')
    const res = await this.chat.sendMessage(convId, {
      text: body.text, client_id: body.client_id, from_ai_draft: body.source === 'ai',
      canned_id: body.source === 'canned' ? body.canned_id : null,
    })
    this.markDone(c, 'sent')
    const conversation = await this.chat.updateConversation(convId, {})
    return { message: res.message, conversation }
  }

  async skipFollowUp(convId: string) {
    await this.delay()
    const c = this.conv(convId)
    if (tabOf(c, this.now()) !== 'follow_up') {
      throw new ApiError(409, 'แชทนี้ไม่อยู่ในคิวทักตามแล้ว (ลูกค้าตอบกลับมาแล้ว หรือทักตามไปแล้ว)')
    }
    this.markDone(c, 'skipped')
    const conversation = await this.chat.updateConversation(convId, {})
    return { conversation: conversation ?? toSummary(c) }
  }

  async setFollowUpAuto(convId: string, enabled: boolean) {
    await this.delay()
    this.conv(convId)
    if (enabled && !this.values.auto_allowed) {
      throw new ApiError(403, 'ยังไม่เปิดให้ตั้งทักตามอัตโนมัติรายแชท (ตั้งค่า › การเตือนและทักตาม)')
    }
    if (enabled) this.autoOn.add(convId)
    else this.autoOn.delete(convId)
    return { enabled }
  }

  // ---------------------------------------------------------------- screens / tests
  /** A colleague answers this chat now (the collision guard's case): same path as a real send. */
  async simulateColleagueReply(convId: string, staffId: string, text: string): Promise<void> {
    const db = this.chat.db
    const me = db.meId
    db.meId = staffId
    try {
      await this.chat.sendMessage(convId, { text, client_id: `sim-${++this.seq}`, from_ai_draft: false, canned_id: null })
    } finally {
      db.meId = me
    }
  }

  // ---------------------------------------------------------------- settings
  private settings(): AlertSettings {
    const ownerOnly: (keyof AlertValues)[] = ['auto_send', 'meta_human_agent']
    const can = Object.fromEntries(
      (Object.keys(this.values) as (keyof AlertValues)[]).map((k) => [
        k,
        this.role === 'owner' || (this.role === 'head' && !ownerOnly.includes(k)),
      ]),
    ) as AlertSettings['can_edit']
    return clone({
      values: this.values,
      can_edit: can,
      owner_only: ownerOnly,
      status: {
        telegram_ready: true,
        send_mode: 'allowlist',
        line: [{ account: 'U6fce0fd22747e9da999b7dd577cbd021', name: 'test', push: false }],
        meta_accounts: 1,
        follow_up_window_days: 7,
        ai_available: true,
      },
    })
  }

  async getAlertSettings(): Promise<AlertSettings> {
    await this.delay()
    return this.settings()
  }

  async putAlertSettings(changes: Partial<AlertValues>): Promise<AlertSettings> {
    await this.delay()
    const s = this.settings()
    for (const k of Object.keys(changes) as (keyof AlertValues)[]) {
      if (!(k in this.values)) throw new ApiError(422, `ไม่รู้จักค่าตั้ง: ${k}`)
      if (!s.can_edit[k]) throw new ApiError(403, 'ค่านี้แก้ได้เฉพาะเจ้าของ')
    }
    if (changes.overdue_min !== undefined && (changes.overdue_min < 1 || changes.overdue_min > 120)) {
      throw new ApiError(422, 'ค่าต้องอยู่ระหว่าง 1 ถึง 120')
    }
    if (changes.follow_up_template !== undefined && !changes.follow_up_template.trim()) {
      throw new ApiError(422, 'ข้อความทักตามตั้งต้นต้องไม่ว่าง')
    }
    this.values = { ...this.values, ...changes }
    // the inbox screen reads these through bootstrap: keep the mock chat backend in step
    this.chat.db.settings.reply_crit_sec = this.values.overdue_min * 60
    this.chat.db.settings.reply_warn_sec = Math.max(30, Math.floor((this.values.overdue_min * 60 * 2) / 3))
    this.chat.db.settings.follow_up_after_min = this.values.follow_up_after_min
    emit({ type: 'settings.changed', section: 'alerts' })
    return this.settings()
  }
}
